import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Save, RefreshCw } from "lucide-react";
import { TeamMemberData, UnitMemberData } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface FinalBandingTableProps {
  teamMembersData: TeamMemberData[] | UnitMemberData[];
  isSavingAll: boolean;
  onSaveAll: () => void;
  onRowSave: (member: TeamMemberData) => void;
  onRowEdit: (memberId: string) => void;
  onCommentsChange: (memberId: string, comments: string) => void;
  isReleasedBanding?: boolean;
  isUnitHead?: boolean;
  isManageUnitTab?: boolean;
  onRefreshStatuses?: () => Promise<void>;
}

const FinalBandingTable = ({
  teamMembersData,
  isSavingAll,
  onSaveAll,
  onRowSave,
  onRowEdit,
  onCommentsChange,
  isReleasedBanding = false,
  isUnitHead = false,
  isManageUnitTab = false,
  onRefreshStatuses
}: FinalBandingTableProps) => {
  const [savingComments, setSavingComments] = useState<Record<string, boolean>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isProcessingSave, setIsProcessingSave] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { activeYear } = usePerformanceYear();

  const sortedTeamMembersData = [...teamMembersData].sort((a, b) => 
    a.full_name.localeCompare(b.full_name)
  );

  // Helper function to check if any team member has a status that shows badges
  const hasAnyBadgeShowingStatus = (): boolean => {
    return sortedTeamMembersData.some(member => {
      const status = member.status;
      return status === 'Final Band Accepted' || 
             status === 'Band disagreement submitted' || 
             status === 'Band disagreement resolution completed' ||
             status === 'Final Banding Done';
    });
  };

  // Helper function to check if all team members have non-empty supervisor comments
  const hasAllSupervisorComments = (): boolean => {
    return sortedTeamMembersData.every(member => {
      const comments = member.supervisorBandingComments || '';
      return comments.trim() !== '';
    });
  };

  const handleRefreshStatuses = async () => {
    if (!isUnitHead || !isManageUnitTab || !onRefreshStatuses) return;
    
    setIsRefreshing(true);
    try {
      console.log('Refreshing status values via parent component...');
      await onRefreshStatuses();
      toast.success('Status values refreshed successfully');
    } catch (error) {
      console.error('Error refreshing statuses:', error);
      toast.error('Failed to refresh status values');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCommentSave = async (memberId: string, comments: string) => {
    setSavingComments(prev => ({ ...prev, [memberId]: true }));
    
    try {
      const { error } = await supabase
        .from('performance_bands')
        .update({
          supervisor_banding_comments: comments,
          updated_at: new Date().toISOString()
        })
        .eq('employee_id', memberId)
        .eq('year', activeYear);

      if (error) {
        console.error('Error saving supervisor comments:', error);
      }
    } catch (error) {
      console.error('Error saving supervisor comments:', error);
    } finally {
      setSavingComments(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, memberId: string, comments: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCommentSave(memberId, comments);
    }
  };

  const handleBlur = (memberId: string, comments: string) => {
    handleCommentSave(memberId, comments);
  };

  const handleSaveBanding = () => {
    if (!isUnitHead || !isManageUnitTab) {
      onSaveAll();
      return;
    }

    const membersWithoutBanding = sortedTeamMembersData.filter(member => 
      !member.finalBanding || member.finalBanding === '-' || member.finalBanding.trim() === ''
    );

    if (membersWithoutBanding.length > 0) {
      toast.error("Not all unit team members have been given an adjusted banding. Please ensure all team members have adjusted banding values before saving.");
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmSaveBanding = async () => {
    setIsProcessingSave(true);
    setShowConfirmDialog(false);

    try {
      const updatePromises = sortedTeamMembersData.map(member => 
        supabase
          .from('performance_bands')
          .update({
            final_banding: member.finalBanding,
            allocated_performance_band: member.allocatedPerformanceBand,
            status: 'Final Banding Done',
            updated_at: new Date().toISOString()
          })
          .eq('employee_id', member.id)
          .eq('year', activeYear)
      );

      const results = await Promise.all(updatePromises);
      
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        console.error('Errors saving performance bands:', errors);
        toast.error("Some performance bands could not be saved. Please try again.");
        return;
      }

      toast.success("Performance banding has been saved successfully!");
      
      onSaveAll();
      
    } catch (error) {
      console.error('Error saving performance bands:', error);
      toast.error("An error occurred while saving performance bands. Please try again.");
    } finally {
      setIsProcessingSave(false);
    }
  };

  const getSafeRating = (member: TeamMemberData | UnitMemberData, ratingType: 'self' | 'manager'): number => {
    let rating: number | undefined | null = 0;
    
    if (ratingType === 'self') {
      if ('self_rating' in member) {
        rating = member.self_rating;
      } else if ('averageSelfRating' in member) {
        rating = member.averageSelfRating;
      }
    } else {
      if ('manager_rating' in member) {
        rating = member.manager_rating;
      } else if ('averageManagerRating' in member) {
        rating = member.averageManagerRating;
      }
    }
    
    return typeof rating === 'number' && !isNaN(rating) ? rating : 0;
  };

  const getStatusBadge = (status: string | null | undefined) => {
    if (status === 'Final Band Accepted') {
      return (
        <Badge className="bg-green-600/20 text-green-400 font-bold text-xs ml-2">
          Accepted
        </Badge>
      );
    }
    if (status === 'Band disagreement submitted') {
      return (
        <Badge className="bg-red-600/20 text-red-400 font-bold text-xs ml-2">
          Disagreement
        </Badge>
      );
    }
    if (status === 'Band disagreement resolution completed') {
      return (
        <Badge className="bg-blue-600/20 text-blue-400 font-bold text-xs ml-2">
          Resolved
        </Badge>
      );
    }
    if (status === 'Final Banding Done') {
      return (
        <Badge className="bg-primary/20 text-primary font-bold text-xs ml-2">
          Released
        </Badge>
      );
    }
    return null;
  };

  const shouldDisableComments = isUnitHead && isManageUnitTab;
  
  const shouldDisableSaveButton = isReleasedBanding && !(isUnitHead && isManageUnitTab);
  
  const hasFinalBandingDone = sortedTeamMembersData.some(member => member.status === 'Final Banding Done');

  // New conditions for button disable logic
  const hasAnyBadges = hasAnyBadgeShowingStatus();
  const hasAllComments = hasAllSupervisorComments();
  
  // Button should be disabled if:
  // 1. Any existing conditions are true, OR
  // 2. Any team member has badge-showing status, OR  
  // 3. Any team member is missing supervisor comments
  const shouldDisableButton = isSavingAll || shouldDisableSaveButton || hasFinalBandingDone || isProcessingSave || hasAnyBadges || !hasAllComments;

  return (
    <Card className="bg-secondary border-primary border flex flex-col h-[340px]">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <CardTitle className="text-white">Calibration</CardTitle>
            {isReleasedBanding && (
              <Badge className="bg-green-500/20 text-green-300 text-[10px] border-green-500/50 px-2 py-1">
                Performance bands released
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-red-500 text-[12px] py-0 mx-[25px]">(Status of "Accepted", "Disagreement" or "Resolved" cannot be changed. Supervisors must provide comments for their team members before the unit head can conduct band distribution. The "Save and release banding" button will not work until all team members have supervisor comments)</span>
            <Button 
              onClick={handleSaveBanding} 
              variant="default" 
              size="sm"
              disabled={shouldDisableButton}
            >
              {(isSavingAll || isProcessingSave) ? 'Saving...' : 'Save and Release Banding'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        {sortedTeamMembersData.length > 0 ? (
          <div className="h-full overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-secondary z-10">
                <TableRow className="border-primary/20 hover:bg-secondary/30">
                  <TableHead className="text-primary text-[10px] w-[3%]">SN</TableHead>
                  <TableHead className="text-primary text-[10px] w-[20%]">Employee ID</TableHead>
                  <TableHead className="text-primary text-[10px] w-[15%]">Team Member Name</TableHead>
                  <TableHead className="text-primary text-[10px] w-[10%]">Self Rating</TableHead>
                  <TableHead className="text-primary text-[10px] w-[10%]">Manager Rating</TableHead>
                  <TableHead className="text-primary text-[10px] w-[13%]">System Banding</TableHead>
                  <TableHead className="text-primary text-[10px] w-[11%]">
                    <div className="flex items-center justify-between">
                      <span>Bell Curve Band</span>
                      {isUnitHead && isManageUnitTab && (
                        <Button
                          onClick={handleRefreshStatuses}
                          variant="ghost"
                          size="sm"
                          disabled={isRefreshing}
                          className="h-6 w-6 p-0 ml-1 hover:bg-primary/10"
                          title="Refresh status values"
                        >
                          <RefreshCw className={`h-3 w-3 text-primary ${isRefreshing ? 'animate-spin' : ''}`} />
                        </Button>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-primary text-[10px] w-[18%]">Supervisor Comments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTeamMembersData.map((member, index) => {
                  const isFinalBandAccepted = member.status === 'Final Band Accepted';
                  const isBandDisagreementSubmitted = member.status === 'Band disagreement submitted';
                  const isBandDisagreementResolved = member.status === 'Band disagreement resolution completed';
                  
                  const selfRating = getSafeRating(member, 'self');
                  const managerRating = getSafeRating(member, 'manager');
                  
                  return (
                    <TableRow key={member.id} className="border-primary/10 hover:bg-secondary/20">
                      <TableCell className="text-white text-[10px]">{index + 1}</TableCell>
                      <TableCell className="text-white text-[10px]">{member.id}</TableCell>
                      <TableCell className="text-white text-[10px]">{member.full_name}</TableCell>
                      <TableCell className="text-white text-[10px]">
                        {selfRating > 0 ? selfRating.toFixed(2) : '-'}
                      </TableCell>
                      <TableCell className="text-white text-[10px]">
                        {managerRating > 0 ? managerRating.toFixed(2) : '-'}
                      </TableCell>
                      <TableCell>
                        {member.allocatedPerformanceBand === '-' ? (
                          <span className="text-white text-[10px]">-</span>
                        ) : (
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                            member.allocatedPerformanceBand === 'A' ? 'bg-blue-500 text-white' :
                            member.allocatedPerformanceBand === 'B' ? 'bg-green-500 text-white' :
                            member.allocatedPerformanceBand === 'C' ? 'bg-yellow-500 text-black' :
                            'bg-red-500 text-white'
                          }`}>
                            {member.allocatedPerformanceBand}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                            member.finalBanding === 'A' ? 'bg-blue-500 text-white' :
                            member.finalBanding === 'B' ? 'bg-green-500 text-white' :
                            member.finalBanding === 'C' ? 'bg-yellow-500 text-black' :
                            member.finalBanding === 'D' ? 'bg-red-500 text-white' :
                            'bg-gray-500 text-white'
                          }`}>
                            {member.finalBanding || '-'}
                          </span>
                          {isManageUnitTab && getStatusBadge(member.status)}
                          {!isManageUnitTab && (
                            <div className="flex items-center gap-2 ml-2">
                              {isFinalBandAccepted && (
                                <Badge className="bg-green-600/20 text-green-400 font-bold text-xs">
                                  Accepted
                                </Badge>
                              )}
                              {isBandDisagreementSubmitted && (
                                <Badge className="bg-red-600/20 text-red-400 font-bold text-xs">
                                  Disagreement
                                </Badge>
                              )}
                              {isBandDisagreementResolved && (
                                <Badge className="bg-blue-600/20 text-blue-400 font-bold text-xs">
                                  Resolved
                                </Badge>
                              )}
                              {member.finalBanding === 'D' && (
                                <Badge className="bg-orange-500/20 text-orange-300 text-[8px] border-orange-500/50 px-1 py-0">
                                  PIP
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-white text-[10px]">
                        <div className="relative">
                          <textarea 
                            value={member.supervisorBandingComments || ''} 
                            onChange={e => onCommentsChange(member.id, e.target.value)}
                            onKeyDown={e => handleKeyDown(e, member.id, member.supervisorBandingComments || '')}
                            onBlur={() => handleBlur(member.id, member.supervisorBandingComments || '')}
                            disabled={shouldDisableComments}
                            className={`w-full h-8 text-[10px] border border-primary/20 rounded px-1 py-0.5 text-white resize-none ${
                              shouldDisableComments 
                                ? 'bg-gray-600/30 cursor-not-allowed opacity-50' 
                                : 'bg-secondary/30'
                            }`}
                            placeholder={shouldDisableComments ? "Comments locked" : "Add comments..."} 
                          />
                          {savingComments[member.id] && (
                            <div className="absolute top-0 right-1 text-green-400 text-[8px]">
                              Saving...
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-400">No team members found or no performance data available.</p>
          </div>
        )}
      </CardContent>
      
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Performance Banding</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save the performance banding for all unit team members? 
              This action will update the status to "Final Banding Done" and team members will be able to view their performance bands.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSaveBanding}>
              Confirm Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default FinalBandingTable;
